/**
 * Microphone choice for dictation and voice calls. Browsers record from the
 * system default input, and macOS readily makes a nearby iPhone (Continuity)
 * the default. The default is kept unless it is a phone; then the built-in
 * microphone, or else any other non-phone input, is used instead.
 */

import { isMicrophonePermissionError } from './microphone-permission-error';

type AudioInput = { deviceId: string; label: string };

const PHONE_MICROPHONE = /iphone|ipad|continuity/i;
const BUILT_IN_MICROPHONE = /built-?in|macbook|internal/i;
/** Chromium's aliases for "whatever the system default is". */
const ALIAS_IDS = new Set(['default', 'communications']);

export function isPhoneMicrophone(label: string): boolean {
  return PHONE_MICROPHONE.test(label);
}

/**
 * The device to request instead of the default, or null to keep the default
 * (it isn't a phone, or labels aren't readable yet before permission).
 */
export function choosePreferredMicrophone(
  inputs: readonly AudioInput[],
  currentLabel?: string
): string | null {
  const defaultLabel =
    currentLabel ??
    inputs.find((input) => input.deviceId === 'default')?.label ??
    '';
  if (!isPhoneMicrophone(defaultLabel)) return null;
  const candidates = inputs.filter(
    (input) =>
      input.label &&
      !ALIAS_IDS.has(input.deviceId) &&
      !isPhoneMicrophone(input.label)
  );
  const builtIn = candidates.find((input) =>
    BUILT_IN_MICROPHONE.test(input.label)
  );
  return (builtIn ?? candidates[0])?.deviceId ?? null;
}

async function audioInputs(): Promise<AudioInput[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === 'audioinput')
    .map(({ deviceId, label }) => ({ deviceId, label }));
}

async function openDevice(
  audio: MediaTrackConstraints,
  deviceId: string
): Promise<MediaStream | null> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { ...audio, deviceId: { exact: deviceId } },
    });
  } catch (error) {
    if (isMicrophonePermissionError(error)) throw error;
    return null;
  }
}

/** getUserMedia for audio, steering away from a phone default. */
export async function getPreferredMicrophoneStream(
  audio: MediaTrackConstraints = {}
): Promise<MediaStream> {
  const media = navigator.mediaDevices;
  // With permission already granted, labels are readable: pick up front.
  const preferredId = choosePreferredMicrophone(
    await audioInputs().catch(() => [])
  );
  if (preferredId) {
    const stream = await openDevice(audio, preferredId);
    if (stream) return stream;
  }
  const stream = await media.getUserMedia({
    audio: Object.keys(audio).length > 0 ? audio : true,
  });
  // First use: labels appear only now, so check what the default gave us.
  // Never fail past this point: the open stream must reach the caller.
  try {
    const label = stream.getTracks()[0]?.label ?? '';
    const switchId = choosePreferredMicrophone(await audioInputs(), label);
    if (!switchId) return stream;
    const switched = await openDevice(audio, switchId);
    if (!switched) return stream;
    stream.getTracks().forEach((track) => track.stop());
    return switched;
  } catch {
    return stream;
  }
}
